import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  ChevronDown,
  ChevronsUpDown,
  ChevronUp,
  Edit,
  ExternalLink,
  Eye,
  Loader2,
  Mail,
  Search,
  Target,
  Trash2,
  UserMinus,
  UserPlus,
  XCircle,
} from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import Badge from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import ConfirmWithTypedNameDialog from '@/components/ui/confirm-with-typed-name-dialog';
import { Input } from '@/components/ui/input';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/components/ui/use-toast';
import config from '@/config';
import AdminReferralSubmissionForm from '@/features/admins/components/AdminReferralSubmissionForm';
import {
  adminCancelSubscription,
  AdminFoundersKeys,
  adminOffboardNow,
  deleteAdminFounder,
  getAdminFounders,
} from '@/features/founders/api/admin-founders';
import CancelSubscriptionDialog from '@/features/founders/components/CancelSubscriptionDialog';
enum StackLevel {
  STARTER = 'starter',
  CERTIFIED = 'certified',
  PRO = 'pro',
  X = 'x',
}

import AssignMentorDialog from './AssignMentorDialog';
import FounderDetailsDialog from './FounderDetailsDialog';
import UpdateProgressionDialog from './UpdateProgressionDialog';

interface FoundersTableProps {
  setFounderId: (id: string) => void;
  onSendStripeReminder: (id: string) => void;
}

const FoundersTable: React.FC<FoundersTableProps> = ({
  setFounderId,
  onSendStripeReminder,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [subscriptionActiveFilter, setSubscriptionActiveFilter] = useState<
    boolean | undefined
  >(true);
  const [levelFilter, setLevelFilter] = useState<string | undefined>(undefined);
  const [viewFounderId, setViewFounderId] = useState<string | null>(null);
  const [deleteFounder, setDeleteFounder] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [progressionFounder, setProgressionFounder] = useState<{
    id: string;
    name: string;
    hasSharedDemo?: boolean;
    hasCompletedPlaybook?: boolean;
  } | null>(null);
  const [assignMentorFounder, setAssignMentorFounder] = useState<{
    id: string;
    name: string;
    currentMentorId?: string | null;
  } | null>(null);
  // STA-743: row actions for cancel + immediate offboard.
  const [cancelSubscriptionFounder, setCancelSubscriptionFounder] = useState<{
    id: string;
    name: string;
    endDateLabel: string;
  } | null>(null);
  const [offboardNowFounder, setOffboardNowFounder] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to first page on search
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const skip = (currentPage - 1) * itemsPerPage;

  const { data, isLoading, error } = useQuery({
    queryKey: [
      AdminFoundersKeys.GET_FOUNDERS,
      debouncedSearchTerm,
      sortBy,
      sortOrder,
      skip,
      itemsPerPage,
      subscriptionActiveFilter,
      levelFilter,
    ],
    queryFn: () =>
      getAdminFounders(
        debouncedSearchTerm,
        sortBy,
        sortOrder,
        skip,
        itemsPerPage,
        subscriptionActiveFilter,
        levelFilter,
      ),
  });

  const handleSort = (column: string) => {
    if (sortBy === column) {
      // If clicking the same column, toggle the order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a different column, set new sort column and default to asc
      setSortBy(column);
      setSortOrder('asc');
    }
    setCurrentPage(1); // Reset to first page on sort
  };

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(Number(value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const getSortIcon = (column: string) => {
    if (sortBy !== column) {
      return <ChevronsUpDown className="ml-2 h-4 w-4" />;
    }
    return sortOrder === 'asc' ? (
      <ChevronUp className="ml-2 h-4 w-4" />
    ) : (
      <ChevronDown className="ml-2 h-4 w-4" />
    );
  };

  const founders = data?.items || [];
  const totalItems = data?.total || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const deleteMutation = useMutation({
    mutationFn: deleteAdminFounder,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [AdminFoundersKeys.GET_FOUNDERS],
      });
      toast({
        title: 'Success',
        description: 'Founder deleted successfully',
      });
      setDeleteFounder(null);
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description:
          error.response?.data?.message || 'Failed to delete founder',
        variant: 'destructive',
      });
    },
  });

  // STA-743: admin "Offboard Immediately" — runs cancel + offboard back-to-back.
  const offboardNowMutation = useMutation({
    mutationFn: (id: string) => adminOffboardNow(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [AdminFoundersKeys.GET_FOUNDERS],
      });
      toast({
        title: 'Offboarding triggered',
        description:
          "Each step's outcome is posted to #founder-updates. Re-check Slack for any failures.",
      });
      setOffboardNowFounder(null);
    },
    onError: (error: any) => {
      toast({
        title: 'Offboard failed',
        description:
          error?.response?.data?.message ||
          error?.message ||
          'Could not offboard founder',
        variant: 'destructive',
      });
    },
  });

  return (
    <Card>
      <div className="p-4 border-b">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative max-w-sm flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search founders by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex gap-2">
            <Select
              value={
                subscriptionActiveFilter === undefined
                  ? 'all'
                  : subscriptionActiveFilter
                    ? 'active'
                    : 'inactive'
              }
              onValueChange={(value) => {
                setSubscriptionActiveFilter(
                  value === 'all'
                    ? undefined
                    : value === 'active'
                      ? true
                      : false,
                );
                setCurrentPage(1);
              }}
            >
              <SelectTrigger
                className="w-[180px]"
                aria-label="Subscription Status"
              >
                <SelectValue placeholder="Subscription Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Subscriptions</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={levelFilter || 'all'}
              onValueChange={(value) => {
                setLevelFilter(value === 'all' ? undefined : value);
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="w-[180px]" aria-label="Level">
                <SelectValue placeholder="Level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Levels</SelectItem>
                <SelectItem value={StackLevel.STARTER}>Starter</SelectItem>
                <SelectItem value={StackLevel.CERTIFIED}>Certified</SelectItem>
                <SelectItem value={StackLevel.PRO}>Pro</SelectItem>
                <SelectItem value={StackLevel.X}>X</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      {isLoading ? (
        <div className="p-8 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead
                className="cursor-pointer select-none"
                onClick={() => handleSort('firstName')}
              >
                <div className="flex items-center">
                  Name
                  {getSortIcon('firstName')}
                </div>
              </TableHead>
              <TableHead
                className="cursor-pointer select-none"
                onClick={() => handleSort('email')}
              >
                <div className="flex items-center">
                  Email
                  {getSortIcon('email')}
                </div>
              </TableHead>
              <TableHead
                className="cursor-pointer select-none"
                onClick={() => handleSort('createdAt')}
              >
                <div className="flex items-center">
                  Created Date
                  {getSortIcon('createdAt')}
                </div>
              </TableHead>
              <TableHead
                className="cursor-pointer select-none"
                onClick={() => handleSort('subscriptionActive')}
              >
                <div className="flex items-center">
                  Active Subscription
                  {getSortIcon('subscriptionActive')}
                </div>
              </TableHead>
              <TableHead
                className="cursor-pointer select-none"
                onClick={() => handleSort('stripeActive')}
              >
                <div className="flex items-center">
                  Stripe Activated
                  {getSortIcon('stripeActive')}
                </div>
              </TableHead>
              <TableHead
                className="cursor-pointer select-none"
                onClick={() => handleSort('accountActivated')}
              >
                <div className="flex items-center">
                  Account Activated
                  {getSortIcon('accountActivated')}
                </div>
              </TableHead>
              <TableHead>Profile</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {founders.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="text-center py-8 text-muted-foreground"
                >
                  {debouncedSearchTerm
                    ? `No founders found matching "${debouncedSearchTerm}"`
                    : 'No founders found'}
                </TableCell>
              </TableRow>
            ) : (
              founders.map((founder) => (
                <TableRow key={founder._id}>
                  <TableCell
                    className="cursor-pointer"
                    onClick={() => setViewFounderId(founder._id)}
                  >
                    {`${founder.firstName} ${founder.lastName}`}
                  </TableCell>
                  <TableCell
                    className="cursor-pointer"
                    onClick={() => setViewFounderId(founder._id)}
                  >
                    {founder.email}
                  </TableCell>
                  <TableCell
                    className="cursor-pointer"
                    onClick={() => setViewFounderId(founder._id)}
                  >
                    {founder.createdAt
                      ? format(new Date(founder.createdAt), 'dd/MM/yyyy')
                      : 'N/A'}
                  </TableCell>
                  <TableCell
                    className="cursor-pointer"
                    onClick={() => setViewFounderId(founder._id)}
                  >
                    <Badge
                      color={
                        founder.subscriptionActive ? 'purple' : 'lightGrey'
                      }
                      variant={founder.subscriptionActive ? 'full' : 'outline'}
                    >
                      {founder.subscriptionActive ? 'Yes' : 'No'}
                    </Badge>
                  </TableCell>
                  <TableCell
                    className="cursor-pointer"
                    onClick={() => setViewFounderId(founder._id)}
                  >
                    <Badge
                      color={founder.stripeActive ? 'purple' : 'lightGrey'}
                      variant={founder.stripeActive ? 'full' : 'outline'}
                    >
                      {founder.stripeActive ? 'Yes' : 'No'}
                    </Badge>
                  </TableCell>
                  <TableCell
                    className="cursor-pointer"
                    onClick={() => setViewFounderId(founder._id)}
                  >
                    <Badge
                      color={founder.accountActivated ? 'purple' : 'lightGrey'}
                      variant={founder.accountActivated ? 'full' : 'outline'}
                    >
                      {founder.accountActivated ? 'Yes' : 'No'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {founder.marketplaceProfile ? (
                      <a
                        href={`${config.marketplaceUrl}/consultant/${founder.marketplaceProfile._id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-primary hover:underline"
                      >
                        <ExternalLink className="h-3 w-3" />
                        View
                      </a>
                    ) : (
                      <span className="text-muted-foreground text-sm">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setViewFounderId(founder._id)}
                        className="gap-1"
                      >
                        <Eye className="h-3 w-3" />
                        View
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setFounderId(founder._id)}
                        className="gap-1"
                      >
                        <Edit className="h-3 w-3" />
                        Edit
                      </Button>
                      {!founder.stripeActive && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onSendStripeReminder(founder._id)}
                          className="gap-1"
                        >
                          <Mail className="h-3 w-3" />
                          Stripe Reminder
                        </Button>
                      )}
                      {founder.subscriptionActive && (
                        <>
                          <AdminReferralSubmissionForm
                            preselectedFounderId={founder._id}
                          >
                            <Button
                              variant="outline"
                              size="sm"
                              className="gap-1"
                            >
                              <UserPlus className="h-3 w-3" />
                              Refer Lead
                            </Button>
                          </AdminReferralSubmissionForm>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setProgressionFounder({
                                id: founder._id,
                                name: `${founder.firstName} ${founder.lastName}`,
                                hasSharedDemo: founder.hasSharedDemo,
                                hasCompletedPlaybook:
                                  founder.hasCompletedPlaybook,
                              })
                            }
                            className="gap-1"
                          >
                            <Target className="h-3 w-3" />
                            Progression
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setAssignMentorFounder({
                                id: founder._id,
                                name: `${founder.firstName} ${founder.lastName}`,
                                currentMentorId:
                                  typeof founder.mentor === 'string'
                                    ? founder.mentor
                                    : founder.mentor?._id,
                              })
                            }
                            className="gap-1"
                          >
                            Assign Mentor
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            // Disable when the founder already cancelled —
                            // re-clicking would re-persist requestedAt and
                            // re-fire the Slack alert. Admin can still
                            // "Offboard Immediately" to skip the cron wait.
                            disabled={
                              !!founder.subscriptionCancellationRequestedAt
                            }
                            title={
                              founder.subscriptionCancellationRequestedAt
                                ? 'Cancellation already requested — pending offboarding'
                                : undefined
                            }
                            onClick={() => {
                              // Prefer the server's stored subscriptionEndsAt
                              // (set the moment cancellation was requested);
                              // otherwise use subscriptionValidUntil — anchored
                              // to onboarding + 1y on the server — which is the
                              // canonical pre-cancel renewal date.
                              let endDateLabel = 'the renewal date';
                              if (founder.subscriptionEndsAt) {
                                endDateLabel = format(
                                  new Date(founder.subscriptionEndsAt),
                                  'MMM d, yyyy',
                                );
                              } else if (founder.subscriptionValidUntil) {
                                endDateLabel = format(
                                  new Date(founder.subscriptionValidUntil),
                                  'MMM d, yyyy',
                                );
                              }
                              setCancelSubscriptionFounder({
                                id: founder._id,
                                name: `${founder.firstName} ${founder.lastName}`,
                                endDateLabel,
                              });
                            }}
                            className="gap-1"
                          >
                            <XCircle className="h-3 w-3" />
                            Cancel Subscription
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setOffboardNowFounder({
                                id: founder._id,
                                name: `${founder.firstName} ${founder.lastName}`,
                              })
                            }
                            className="gap-1 text-destructive hover:text-destructive"
                          >
                            <UserMinus className="h-3 w-3" />
                            Offboard Immediately
                          </Button>
                        </>
                      )}
                      {!founder.subscriptionActive && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setDeleteFounder({
                              id: founder._id,
                              name: `${founder.firstName} ${founder.lastName}`,
                            })
                          }
                          className="gap-1 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-3 w-3" />
                          Delete
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}
      {founders.length > 0 && (
        <div className="border-t px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Show</span>
              <Select
                value={itemsPerPage.toString()}
                onValueChange={handleItemsPerPageChange}
              >
                <SelectTrigger className="h-8 w-[70px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">entries</span>
            </div>
            <span className="text-sm text-muted-foreground">
              Showing {skip + 1} to {Math.min(skip + itemsPerPage, totalItems)}{' '}
              of {totalItems} founders
            </span>
          </div>
          {totalPages > 1 && (
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                    className={
                      currentPage === 1
                        ? 'pointer-events-none opacity-50'
                        : 'cursor-pointer'
                    }
                  />
                </PaginationItem>
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  // Show first page, last page, current page, and pages around current
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 &&
                      pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <PaginationItem key={pageNumber}>
                        <PaginationLink
                          onClick={() => setCurrentPage(pageNumber)}
                          isActive={currentPage === pageNumber}
                          className="cursor-pointer"
                        >
                          {pageNumber}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  } else if (
                    pageNumber === currentPage - 2 ||
                    pageNumber === currentPage + 2
                  ) {
                    return (
                      <PaginationItem key={pageNumber}>
                        <span className="px-2">...</span>
                      </PaginationItem>
                    );
                  }
                  return null;
                })}
                <PaginationItem>
                  <PaginationNext
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                    }
                    className={
                      currentPage === totalPages
                        ? 'pointer-events-none opacity-50'
                        : 'cursor-pointer'
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </div>
      )}
      <FounderDetailsDialog
        founderId={viewFounderId}
        onClose={() => setViewFounderId(null)}
      />

      <UpdateProgressionDialog
        open={!!progressionFounder}
        onOpenChange={(open) => !open && setProgressionFounder(null)}
        licenseeId={progressionFounder?.id || null}
        licenseeName={progressionFounder?.name || ''}
        currentProgression={{
          hasSharedDemo: progressionFounder?.hasSharedDemo,
          hasCompletedPlaybook: progressionFounder?.hasCompletedPlaybook,
        }}
      />

      <AssignMentorDialog
        open={!!assignMentorFounder}
        onOpenChange={(open) => !open && setAssignMentorFounder(null)}
        licenseeId={assignMentorFounder?.id || null}
        licenseeName={assignMentorFounder?.name || ''}
        currentMentorId={assignMentorFounder?.currentMentorId || null}
      />

      {cancelSubscriptionFounder && (
        <CancelSubscriptionDialog
          open={true}
          onOpenChange={(open) => !open && setCancelSubscriptionFounder(null)}
          endDateLabel={cancelSubscriptionFounder.endDateLabel}
          onSubmitOverride={async (payload) => {
            const data = await adminCancelSubscription(
              cancelSubscriptionFounder.id,
              payload,
            );
            // Refresh the founders table so any derived UI (e.g. badges) updates.
            queryClient.invalidateQueries({
              queryKey: [AdminFoundersKeys.GET_FOUNDERS],
            });
            return data;
          }}
        />
      )}

      <ConfirmWithTypedNameDialog
        open={!!offboardNowFounder}
        onOpenChange={(open) => !open && setOffboardNowFounder(null)}
        expectedText={offboardNowFounder?.name || ''}
        title="Offboard founder immediately"
        description={
          <>
            This will immediately deactivate the Circle membership, block the
            Auth0 user, mark <strong>{offboardNowFounder?.name}</strong> as
            inactive, and set Attio Payment Status to Expired. Each step posts
            its own ✅/❌ to <strong>#founder-updates</strong>. This skips the
            scheduled cron — use only if access must be revoked now.
          </>
        }
        destructiveLabel="Offboard now"
        isPending={offboardNowMutation.isPending}
        onConfirm={() => {
          if (offboardNowFounder) {
            offboardNowMutation.mutate(offboardNowFounder.id);
          }
        }}
      />

      <AlertDialog
        open={!!deleteFounder}
        onOpenChange={() => setDeleteFounder(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the founder "{deleteFounder?.name}".
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                deleteFounder && deleteMutation.mutate(deleteFounder.id)
              }
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default FoundersTable;
